import * as React from 'react';
import FormTable, {FormTableProps} from 'cl-form/FormTable';
import * as _ from 'lodash';
import {DEFAULT_LISTINGS_COLUMNS_PROPS, LISTING_COLUMNS, PRODUCT_LISTINGS_TABLE} from '../consts';
import {ColumnsType} from 'cl-table/redux/actions';
import {useSelector} from 'react-redux';
import {getAmazonListingIdHref} from '../../AmazonListings/consts';
import routes from '../../../App/routeConstants';
import EmptyState from '../../ViewTemplates/containers/EmptyState';
import {ACTION_DISPLAY_TYPES} from '../../../shared/ActionsManager/ActionsManagerConsts';
import GeneralActions from '../../../shared/ActionsManager/GeneralActions/GeneralActions';
import {GENERAL_ACTIONS_TYPES} from '../../../shared/ActionsManager/ActionsTypes';
import {withRouter, WithRouterProps} from 'react-router';
import {productApi, Channels} from 'cl-api';
import {getProductListings} from '../EditProductSelectors';
import {StoreState} from '../../../App/initialStore';
import {useCallback} from 'react';

const ListingsProductSection: React.FunctionComponent<FormTableProps & WithRouterProps> = ({
  params: {id},
  ...props
}) => {
  const productListings = useSelector((state: StoreState) => getProductListings(state));

  const loadProductListings = useCallback(async () => {
    const productListings = await (await productApi.getProductListings({productIds: [id]})).json();
    return {rows: productListings[id], count: _.size(productListings[id])};
  }, [id]);

  const allListingsVariations = React.useMemo(() => {
    let keys = [];
    if (productListings) {
      productListings.forEach(listing => {
        keys = [...keys, ..._.keys(listing[LISTING_COLUMNS.VARIATIONS])];
      });
    }
    return keys;
  }, [productListings]);

  const columns = React.useMemo(() => {
    const columns: ColumnsType = {};
    _.forEach(_.values(LISTING_COLUMNS), columnName => {
      columns[columnName] = {
        ...DEFAULT_LISTINGS_COLUMNS_PROPS[columnName],
        readOnly: true,
      };
    });
    columns[LISTING_COLUMNS.VARIATIONS].composite = _.mapKeys(
      _.keyBy(allListingsVariations),
      (value, key) => `${LISTING_COLUMNS.VARIATIONS}.${key}`
    );
    columns[LISTING_COLUMNS.LISTING_ID].getHref = ({Channel, Asin, Account, ListingUrl, Site}) =>
      Channel === Channels.Amazon ? getAmazonListingIdHref(Asin, Site, Account) : (ListingUrl as string);

    columns[LISTING_COLUMNS.LISTING_TITLE].getHref = ({Channel, Account, ListingId}) =>
      Channel === Channels.EBay
        ? `${routes.MyAccount.EbayListing}?selectedSellerId=${Account}&itemId=${ListingId}&statusFilter=Active`
        : undefined;
    return columns;
  }, [productListings]);

  return (
    <FormTable
      tableName={PRODUCT_LISTINGS_TABLE}
      columns={columns}
      thumbnail
      config={{
        id: LISTING_COLUMNS.LISTING_ID,
        title: LISTING_COLUMNS.LISTING_TITLE,
      }}
      filterProps={{preventMoreFilters: true}}
      actions={{
        right: (
          <GeneralActions
            types={[GENERAL_ACTIONS_TYPES.SYNC_LISTINGS_FROM_CHANNELS]}
            displayType={ACTION_DISPLAY_TYPES.FLAT_BUTTON}
            additionalActionData={[id]}
          />
        ),
      }}
      supportChildren
      noContentRenderer={() => {
        return (
          <EmptyState
            src="noProducts"
            headline={'No listings are linked to this product'}
            paragraphs={['Once this product is published on eBay or Amazon, the listings will appear here']}
          />
        );
      }}
      loadMoreRows={loadProductListings}
      {...props}
    />
  );
};

export default withRouter(ListingsProductSection);
